type ItemHandler<Item> = (i: Item) => Promise<{ success: boolean }>

export const LOCAL_STORAGE_QUEUE_KEY = 'v1_storage_consent_queue'

export class Queue<Item> {
  private isDraining = false
  private itemHandler: ItemHandler<Item>

  constructor(itemHandler: ItemHandler<Item>) {
    this.itemHandler = itemHandler
  }

  /**
   * Gets queued items from local storage
   */
  private getQueuedItems() {
    const unsafeQueuedItems = window.localStorage.getItem(
      LOCAL_STORAGE_QUEUE_KEY,
    )

    return (
      unsafeQueuedItems !== null ? JSON.parse(unsafeQueuedItems) : []
    ) as Item[]
  }

  /**
   * Gets items from local storage,
   * removes first item and updates local storage
   * @returns shifted item
   */
  private shiftQueuedItems() {
    const items = this.getQueuedItems()

    const [shifted, ...restItems] = items

    window.localStorage.setItem(
      LOCAL_STORAGE_QUEUE_KEY,
      JSON.stringify(restItems),
    )

    return shifted
  }

  /**
   * Appends item into local storage array
   */
  public enqueue(item: Item) {
    const existing = this.getQueuedItems()

    const updated = [...existing, item]

    window.localStorage.setItem(
      LOCAL_STORAGE_QUEUE_KEY,
      JSON.stringify(updated),
    )
  }

  public async drain() {
    if (this.isDraining) {
      return
    }

    this.isDraining = true

    let shouldHandleItems = true
    const toRequeue: Item[] = []

    while (shouldHandleItems) {
      const item = this.shiftQueuedItems()
      if (!item) break

      try {
        const { success } = await this.itemHandler(item)
        if (!success) throw new Error('Failed to handle queued item')
      } catch (err) {
        toRequeue.push(item)
      }

      shouldHandleItems = this.getQueuedItems().length > 0
    }

    this.isDraining = false

    // Add failed items back into local storage for handling later
    for (const item of toRequeue) {
      this.enqueue(item)
    }
  }

  public isEmpty() {
    return this.getQueuedItems().length === 0
  }
}
